import type {
    FilterItem,
    StandardFilter as TStandardFilter,
} from '@schibsted-nmp/search-frontend-clients/quest';
import { Toggle } from '@warp-ds/react';
import React from 'react';
import { useFilterSearch } from './useFilterSearch';
import { usePopularItems } from './usePopularItems';

type ToggleEntry = {
    label: string;
    value: string;
};

type StandardFilterProps = {
    filter: TStandardFilter;
};

export function StandardFilter(props: StandardFilterProps) {
    const { filter } = props;

    const { expandCollapseButton, filterItems } = usePopularItems(filter);
    const { selectedItems, updateSelectedItem } = useSelectedItems(filterItems);

    const { submitFilterSearch } = useFilterSearch();

    const handleFilterChange = React.useCallback(
        ({ value }: ToggleEntry) => {
            const changedItem = filterItems.find(
                (item) => item.value === value,
            );
            if (changedItem) {
                updateSelectedItem(changedItem);
                submitFilterSearch(changedItem);
            }
        },
        [filterItems, submitFilterSearch, updateSelectedItem],
    );

    const options: ToggleEntry[] = React.useMemo(() => {
        return filterItems.map((item) => ({
            label: `${item.display_name} (${item.hits})`,
            value: item.value,
        }));
    }, [filterItems]);

    const selectedOptions: ToggleEntry[] = React.useMemo(
        () =>
            selectedItems.map((item) => ({
                label: item.display_name,
                value: item.value,
            })),
        [selectedItems],
    );

    return (
        <>
            <Toggle
                onChange={handleFilterChange}
                options={options}
                selected={selectedOptions}
                type="checkbox"
            />
            {expandCollapseButton}
        </>
    );
}

function useSelectedItems(filterItems: FilterItem[]) {
    const [selectedItems, setSelectedItems] = React.useState(() =>
        filterItems.filter((item) => item.selected),
    );

    React.useEffect(
        function syncFilterState() {
            setSelectedItems(filterItems.filter((item) => item.selected));
        },
        [filterItems],
    );

    const updateSelectedItem = React.useCallback((changedItem: FilterItem) => {
        setSelectedItems((prevItems) => {
            const isSelectedItem = prevItems.includes(changedItem);
            return isSelectedItem
                ? prevItems.filter((item) => item !== changedItem)
                : [...prevItems, changedItem];
        });
    }, []);

    return { selectedItems, updateSelectedItem };
}
